/**
 * Some keys have underscores because values come from API
 *
 * POIs:
 * - From Schools POIs
 * -- high_schools & primary_schools
 *
 * - From Enhanced POIs
 * -- alcohol, clothing, common_needs, conference_centers, entertainment, hospitals, libraries, pharmacies, retail, shops_and_services
 */

const namespace = {
  attribution: "Propulsé par",
  attributionAriaLabel: "Propulsé par Local Logic",
  time: {
    minute_one: "Minute",
    minute_other: "Minutes",
    hour_one: "Heure",
    hour_other: "Heures",
  },
  distance: {
    meter: "m",
    kilometer: "km",
    yard: "vg",
    mile: "mil",
  },
  language: {
    english: "Anglais",
    french: "Français",
    spanish: "Espagnol",
  },
  map: {
    marker: {
      schoolAttributes: {
        levels: "Niveau",
        grades: "Notes",
        languages: "Langues",
        types: "Type",
        board: "Commission",
        programs: "Programmes",
        categories: "Catégories",
        rating: "Note des écoles",
      },
      schoolCta: "Lien vers l'école",
      district: "Commission",
      categories: {
        advancedPlacement: "placement avancé",
        charter: "à charte",
        magnet: "à vocation particulière",
      },
    },
    cluster: {
      poiCount_one: "{{count}} Lieu",
      poiCount_other: "{{count}} Lieux",
    },
  },
  scores: {
    cafes: {
      title: "Cafés",
      description:
        "Une mesure de la quantité et de la diversité des cafés à proximité.",
    },
    car_friendly: {
      title: "Adapté aux voitures",
      description:
        "Représente la fluidité de la circulation et la facilité de stationnement.",
    },
    cycling_friendly: {
      title: "Adapté aux cyclistes",
      description:
        "Représente la qualité de l'aménagement du réseau cyclable autant pour les déplacement que pour le cyclisme récréatif, considérant les pistes et bande cyclables à proximité, le pourcentage d'habitants qui se déplacent à vélo, le dénivelé du terrain ainsi que la présence de vélos en libre-service.",
    },
    daycares: {
      title: "Garderies",
      description: "Représente la distance jusqu'à la garderie la plus proche.",
    },
    greenery: {
      title: "Verdure",
      description:
        "Quantité d'espaces verts à proximité et couverture de la canopée des arbres.",
    },
    groceries: {
      title: "Épiceries",
      description: "Une mesure de la quantité d'épiceries à proximité.",
    },
    high_schools: {
      title: "Écoles secondaires",
      description:
        "Représente la distance jusqu'à l'école secondaire la plus proche.",
    },
    historic: {
      title: "Historique",
      description:
        "Une mesure de la quantité et de la taille des bâtiments patrimoniaux, et de l'âge moyen des bâtiments dans la région.",
    },
    nightlife: {
      title: "Vie nocturne",
      description:
        "Représente la quantité et la diversité des bars à proximité.",
    },
    parks: {
      title: "Parcs",
      description:
        "Une mesure de la quantité et de la taille des parcs à proximité.",
    },
    pedestrian_friendly: {
      title: "Adapté aux piétons",
      description:
        "Mesure la quantité de commerces et de services à distance de marche, à quel point ils sont adaptés aux piétons et à quel point il est agréable de s'y rendre à pied.",
    },
    primary_schools: {
      title: "Écoles primaires",
      description:
        "Une mesure de la distance jusqu'à l'école primaire la plus proche.",
    },
    quiet: {
      title: "Silencieux",
      description: "Une mesure des niveaux moyens de bruit à proximité.",
    },
    restaurants: {
      title: "Restaurants",
      description:
        "Représente la quantité et la diversité des restaurants à proximité.",
    },
    shopping: {
      title: "Magasins",
      description:
        "Une mesure de la quantité et de la diversité de magasins de mode et de grands magasins à proximité.",
    },
    transit_friendly: {
      title: "Transport public",
      description:
        "Une mesure de la fréquence et du niveau de service de transport en commun.",
    },
    vibrant: {
      title: "Dynamisme",
      description: "Représente le niveau d'activité de la rue à proximité.",
    },
    wellness: {
      title: "Bien-être",
      description:
        "Représente à quel point un emplacement favorise le bien-être des individus.",
    },
  },
  pois: {
    airports: "Aéroports",
    alcohol: "Alcool",
    art_culture_science: "Art, culture, science",
    banks: "Banques",
    bus_stops: "Autobus",
    cafes: "Cafés",
    car_fueling_stations: "Stations-service",
    casino: "Casino",
    clothing: "Vêtements",
    common_needs: "Besoins quotidiens",
    conference_centers: "Salles de conférence",
    convenience_stores: "Dépanneurs",
    daycares: "Garderies",
    department_stores: "Grands magasins",
    entertainment: "Divertissement",
    fitness: "Entraînement physique",
    groceries: "Épiceries",
    groceries_specialized: "Spécialisées",
    health_care: "Soins de santé",
    home_improvement: "Magasin de rénovation",
    hospitals: "Hôpitaux",
    libraries: "Librairies",
    nightlife: "Vie nocturne",
    non_food_shops: "Magasinage",
    parks: "Parcs",
    personal_care: "Soins personnels",
    pet_care: "Soins pour animaux",
    pharmacies: "Pharmacies",
    rapid_transit_stations: "Métro",
    restaurants: "Restaurants",
    retail: "Magasins de détail",
    shops_and_services: "Magasins et services",
    sport_facilities: "Installations sportives",
    train_stations: "Train",
    // Schools - from Schools API
    high_schools: "Écoles secondaires",
    primary_schools: "Écoles primaires",
    // Deprecated
    healthy_restaurants: "Restaurants sains",
    shopping: "Magasinage",
  },
  valueDrivers: {
    bus_service_access: "Services d'autobus",
    shops_and_services_access: "Magasins et services",
    coffee_shop_access: "Cafés",
    clothing_and_accessory_store_access: "Magasins de vêtements",
    common_needs_access: "Nécessités communes",
    department_store_access: "Grands magasins",
    entertainment_venue_access: "Lieux de divertissement",
    fitness_establishment_access: "Conditionnement physique",
    nightlife_access: "Vie nocturne",
    pedestrian_oriented_street_access: "Rues accessibles aux piétons",
    rapid_transit_service_access: "Service de transport en commun rapide",
    restaurant_access: "Restaurants",
    specialty_grocery_store_access: "Épiceries spécialisées",
    sports_facility_access: "Installations sportives",
    train_service_access: "Gares",
    nearest_highway_proximity: "Accès à l'autoroute",
    nearest_pharmacy_proximity: "Pharmacies à proximité",
    nearest_bicycle_share_station_proximity:
      "Stations de vélos en libre-service à proximité",
    bicycle_path_access: "Stations de vélopartage à proximité",
    bicycle_share_station_access: "Partage de vélo",
    nearest_park_area: "Grand parc à proximité",
    bicycle_infrastructure_quality: "Infrastructure pour vélos",
    high_schools: "Écoles secondaires",
    primary_schools: "Écoles primaires",
    quiet: "Calme",
    daycares: "Garderies",
    parks: "Parcs",
  },
} as const;

export default namespace;
